import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Truck, RotateCcw, ShoppingBag, BadgeCheck, Loader2, Check } from "lucide-react";
import api from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [buying, setBuying] = useState(false);
  const [wallet, setWallet] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/products/${slug}`);
        setProduct(r.data);
      } catch (e) {
        toast.error("Product not found");
        navigate("/shop/wellness");
      }
    })();
  }, [slug, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/coupons").then((r) => {
      setWallet(r.data.filter((c) => c.redeemable));
      // auto-apply first redeemable coupon
      if (r.data.length && r.data[0].redeemable && !couponCode) {
        const c = r.data[0];
        setCouponCode(c.code);
        setCouponInfo({ valid: true, discount_percent: c.discount_percent, title: c.title });
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const applyCoupon = async () => {
    if (!user) { toast.error("Please log in to apply a coupon"); return; }
    if (!couponCode.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: couponCode.trim().toUpperCase() });
      if (r.data.valid) {
        setCouponInfo(r.data);
        toast.success(isHi ? "कूपन लागू हुआ" : "Coupon applied");
      } else {
        setCouponInfo(null);
        toast.error(r.data.reason || "Coupon invalid");
      }
    } catch (e) {
      setCouponInfo(null);
      toast.error(e.response?.data?.detail || "Coupon invalid");
    }
  };

  const buyNow = async () => {
    if (!user) { navigate(`/auth?next=/products/${slug}`); return; }
    setBuying(true);
    try {
      const items = [{
        product_id: product.id, slug: product.slug, title: product.title,
        price_inr: product.price_inr, quantity: qty,
      }];
      const r = await api.post("/orders", { items, coupon_code: couponInfo?.valid ? couponCode.toUpperCase() : null });
      toast.success(isHi ? "आदेश दर्ज हुआ" : "Order placed");
      navigate(`/dashboard?tab=orders&order=${r.data.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally {
      setBuying(false);
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="grid place-items-center py-24"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      </div>
    );
  }

  const price = product.price_inr;
  const compare = product.compare_at_inr;
  const discount = couponInfo?.valid ? Math.floor(price * qty * couponInfo.discount_percent / 100) : 0;
  const total = Math.max(0, price * qty - discount);

  return (
    <div className="min-h-screen bg-background">
      <Nav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/shop/${product.category}`} className="text-sm text-muted-foreground inline-flex items-center gap-1.5 hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> {isHi ? "वापस" : "Back to shop"}
        </Link>

        <div className="mt-6 grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <div className="aspect-square rounded-xl overflow-hidden border border-border bg-white">
              <img src={product.image_urls?.[activeImg] || product.image_url} alt={product.title} className="w-full h-full object-cover" />
            </div>
            {product.image_urls && product.image_urls.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {product.image_urls.map((u, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-md overflow-hidden border-2 ${
                      activeImg === i ? "border-primary" : "border-border hover:border-primary/30"
                    }`}
                    data-testid={`gallery-thumb-${i}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="label-eyebrow">{product.collection || product.category}</div>
            <h1 className="mt-2 font-display text-3xl lg:text-4xl font-extrabold tracking-tight">
              {isHi && product.title_hi ? product.title_hi : product.title}
            </h1>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {isHi && product.short_desc_hi ? product.short_desc_hi : product.short_desc}
            </p>

            <div className="mt-6 flex items-baseline gap-3">
              <span data-testid="product-price" className="font-display text-4xl font-extrabold text-primary">₹{price.toLocaleString("en-IN")}</span>
              {compare && <span className="text-sm text-muted-foreground line-through">₹{compare.toLocaleString("en-IN")}</span>}
            </div>

            {/* Highlights */}
            <ul className="mt-6 space-y-2 text-sm">
              {((isHi && product.highlights_hi) || product.highlights || []).map((h, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-accent flex-shrink-0" /> {h}
                </li>
              ))}
            </ul>

            {/* Coupon */}
            <div className="mt-8 rounded-lg border border-border p-4 bg-muted/30">
              <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                {isHi ? "कूपन कोड" : "Coupon code"}
              </div>
              <div className="flex gap-2">
                <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="VYOM-XXXX-XXXX"
                  className="flex-1 px-3 py-2 rounded-md border border-input bg-white text-sm font-mono"
                  data-testid="product-coupon-input"
                />
                <button onClick={applyCoupon} className="btn-primary px-4 rounded-md text-sm font-semibold" data-testid="product-coupon-apply">
                  {isHi ? "लागू करें" : "Apply"}
                </button>
              </div>
              {wallet.length > 0 && (
                <div className="mt-3 text-xs text-muted-foreground">
                  {isHi ? "आपके पास मान्य कूपन:" : "You have available coupons:"}{" "}
                  {wallet.slice(0, 3).map((c) => (
                    <button key={c.code} onClick={() => { setCouponCode(c.code); setCouponInfo({ valid: true, discount_percent: c.discount_percent }); }}
                      className="mx-1 font-mono font-bold text-accent hover-underline">
                      {c.code}
                    </button>
                  ))}
                </div>
              )}
              {couponInfo?.valid && (
                <div className="mt-3 text-sm text-primary font-semibold inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4" /> {couponInfo.discount_percent}% OFF applied
                </div>
              )}
            </div>

            {/* Qty + total + buy */}
            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                  {isHi ? "मात्रा" : "Quantity"}
                </div>
                <div className="inline-flex items-center border border-border rounded-md">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 font-bold text-lg hover:bg-muted" data-testid="product-qty-minus">−</button>
                  <span className="px-4 text-sm font-semibold" data-testid="product-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-2 font-bold text-lg hover:bg-muted" data-testid="product-qty-plus">+</button>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                  {isHi ? "कुल" : "Total"}
                </div>
                <div className="text-2xl font-display font-extrabold text-primary" data-testid="product-total">
                  ₹{total.toLocaleString("en-IN")}
                  {discount > 0 && (
                    <span className="ml-2 text-sm text-accent font-semibold">−₹{discount.toLocaleString("en-IN")}</span>
                  )}
                </div>
              </div>
            </div>

            <button onClick={buyNow} disabled={buying} className="mt-6 w-full btn-primary py-3.5 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="product-buy-btn">
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              {isHi ? "अभी खरीदें" : "Buy Now"}
            </button>

            {/* Trust row */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2"><Truck className="w-4 h-4 mt-0.5 text-primary" /> <span>Free shipping over ₹999</span></div>
              <div className="flex items-start gap-2"><RotateCcw className="w-4 h-4 mt-0.5 text-primary" /> <span>7-day return policy</span></div>
              <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-primary" /> <span>Authentic · Sourced</span></div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
